import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  PaDataTable,
  PaEmptyState,
  PaFilterBar,
  PaFilterChip,
  PaPageHeader,
  PaPagination,
  PaStatusBadge,
} from "components/platform-admin";
import PlatformAdminLayout from "pages/PlatformAdmin/layout/PlatformAdminLayout";
import PaAccessDenied from "pages/PlatformAdmin/layout/PaAccessDenied";
import PaRelativeTime from "pages/PlatformAdmin/PaRelativeTime";
import { paPrimaryButtonClass } from "pages/PlatformAdmin/platformAdminPage.utils";
import { platformRoleAccess } from "helpers/RolesPermission";
import { getPaginationObj } from "Utils/utils";
import {
  fetchPlatformSaleLeads,
  resendPlatformSaleLeadInvite,
} from "store/services/platformAdmin.services";
import { URL_PLATFORM_ADMIN_SALE_LEADS } from "store/services/URL";
import CreateProspectDrawer from "./CreateProspectDrawer";
import {
  buildProspectsListQuery,
  DEFAULT_PROSPECT_FILTERS,
  hasActiveProspectFilters,
  PROSPECT_STATUS_OPTIONS,
} from "./prospectsList.utils";

const ProspectsList = () => {
  const canViewProspects = platformRoleAccess("platform.prospect.list");
  const canCreateProspect = platformRoleAccess("platform.prospect.create");
  const [filters, setFilters] = useState(DEFAULT_PROSPECT_FILTERS);
  const [searchInput, setSearchInput] = useState("");
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(getPaginationObj());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [resendingId, setResendingId] = useState(null);

  const loadProspects = useCallback(async () => {
    if (!canViewProspects) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const endpoint = buildProspectsListQuery(filters, URL_PLATFORM_ADMIN_SALE_LEADS);
      const payload = await fetchPlatformSaleLeads(endpoint);
      const saleLeads = payload?.sale_leads;
      setRows(Array.isArray(saleLeads?.data) ? saleLeads.data : []);
      setPagination(getPaginationObj(saleLeads, filters));
    } catch (err) {
      setRows([]);
      setError(err?.message || "Failed to load prospects");
    } finally {
      setLoading(false);
    }
  }, [canViewProspects, filters]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFilters((prev) => {
        if (prev.search === searchInput) {
          return prev;
        }

        return { ...prev, search: searchInput, page: 1 };
      });
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  useEffect(() => {
    loadProspects();
  }, [loadProspects]);

  const handleClearFilters = () => {
    setSearchInput("");
    setFilters(DEFAULT_PROSPECT_FILTERS);
  };

  const handleResendInvite = useCallback(async (row) => {
    if (!row?.uuid || row.status === "converted") {
      return;
    }

    setResendingId(row.id);
    try {
      await resendPlatformSaleLeadInvite(row.uuid);
      toast.success("Invitation resent");
    } catch (err) {
      toast.error(err?.message || "Failed to resend invite");
    } finally {
      setResendingId(null);
    }
  }, []);

  const statusFilters = useMemo(
    () =>
      PROSPECT_STATUS_OPTIONS.map((option) => (
        <PaFilterChip
          key={option.label}
          active={filters.status === option.value}
          onClick={() =>
            setFilters((prev) => ({
              ...prev,
              status: option.value,
              page: 1,
            }))
          }
        >
          {option.label}
        </PaFilterChip>
      )),
    [filters.status]
  );

  const columns = useMemo(
    () => [
      {
        key: "business_name",
        header: "Business",
        render: (row) => (
          <span className="font-medium text-gray-900">
            {row.business_name || row.name || "—"}
          </span>
        ),
      },
      {
        key: "email",
        header: "Email",
        render: (row) => (
          <span className="text-sm text-gray-700">{row.email || "—"}</span>
        ),
      },
      {
        key: "phone",
        header: "Phone",
        render: (row) => (
          <span className="text-sm text-gray-700">{row.phone || "—"}</span>
        ),
      },
      {
        key: "abn",
        header: "ABN",
        render: (row) => (
          <span className="font-mono text-sm text-gray-600">{row.abn || "—"}</span>
        ),
      },
      {
        key: "status",
        header: "Status",
        render: (row) => (
          <PaStatusBadge
            label={row.status || "lead"}
            variant={row.status === "converted" ? "success" : "neutral"}
          />
        ),
      },
      {
        key: "created_at",
        header: "Created",
        render: (row) => <PaRelativeTime value={row.created_at} />,
      },
      {
        key: "actions",
        header: "Actions",
        headerClassName: "text-right",
        cellClassName: "text-right",
        render: (row) =>
          row.status === "converted" ? (
            <span className="text-xs text-gray-400">Converted</span>
          ) : canCreateProspect ? (
            <button
              type="button"
              disabled={resendingId === row.id}
              onClick={() => handleResendInvite(row)}
              className="rounded-lg px-3 py-1.5 text-xs font-medium text-indigo-600 hover:bg-indigo-50 disabled:opacity-50"
            >
              {resendingId === row.id ? "Sending…" : "Resend invite"}
            </button>
          ) : null,
      },
    ],
    [canCreateProspect, handleResendInvite, resendingId]
  );

  if (!canViewProspects) {
    return (
      <PaAccessDenied
        title="Prospects"
        subtitle="Sale lead funnel"
        message="You don't have access to prospects"
        description="You need platform.prospect.list to view sale leads."
      />
    );
  }

  return (
    <PlatformAdminLayout>
      <PaPageHeader
        title="Prospects"
        subtitle="Sale lead funnel — invite to signup"
        actions={
          canCreateProspect ? (
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className={paPrimaryButtonClass}
            >
              Invite prospect
            </button>
          ) : null
        }
      />

      <PaFilterBar
        className="mb-4"
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        searchPlaceholder="Search business, email, or phone"
        filters={statusFilters}
        onClear={hasActiveProspectFilters(filters) ? handleClearFilters : undefined}
        resultCount={pagination.total}
      />

      {error ? (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <PaDataTable
        columns={columns}
        rows={rows}
        isLoading={loading}
        emptyState={
          <PaEmptyState
            title="No prospects yet"
            message="Start your sale lead funnel"
            description="Invite a service provider to sign up for Vertex360."
            action={
              canCreateProspect ? (
                <button
                  type="button"
                  onClick={() => setCreateOpen(true)}
                  className={paPrimaryButtonClass}
                >
                  Invite prospect
                </button>
              ) : null
            }
          />
        }
      />

      <PaPagination
        page={filters.page}
        totalPages={pagination.totalPages}
        total={pagination.total}
        rows={rows}
        perPage={filters.perPage}
        loading={loading}
        onPageChange={(page) => setFilters((prev) => ({ ...prev, page }))}
      />

      <CreateProspectDrawer
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={loadProspects}
      />
    </PlatformAdminLayout>
  );
};

export default ProspectsList;
